import {
  type AvailableShare,
  type MediaDescriptor,
  type OwnerMediaControlMessage,
  type ShareGrant,
} from "@aasaan-share/protocol";
import type {
  TransferState,
  VideoCapabilityState,
  ViewerAdmissionState,
} from "@aasaan-share/ui-contract";
import {
  AlbumDownloadQueue,
  type AlbumDownloadProgress,
} from "./album-download.ts";
import {
  type BrowserContributionProgress,
  uploadBrowserContribution,
} from "./collaborative-upload.ts";
import {
  type ConnectionClientEvents,
  type ViewerConnectionTransport,
} from "./viewer-transport.ts";
import {
  createDownloadSink,
  MemoryDownloadSink,
  safeDownloadFilename,
  saveVerifiedBlob,
} from "./download-sink.ts";
import { canDownloadVideo, visibleMediaForGrant } from "./feature-capabilities.ts";
import { fixtureFromLocation } from "./fixtures.ts";
import {
  galleryRendition,
  type RenditionName,
} from "./gallery-session.ts";
import { ViewerMediaCache } from "./media-cache.ts";
import { selectedShareLocation } from "./share-location.ts";
import { ShareCatalogue, type ViewerShare } from "./share-catalogue.ts";
import { StorageViewerConnectionClient } from "./storage-connection-client.ts";
import { closeDialog, openDialog } from "./ui/dialog-manager.ts";
import type {
  ViewerMediaModel,
  ViewerContributionModel,
  ViewerShareModel,
  ViewerUiModel,
} from "./ui/models.ts";
import { ViewerApp } from "./ui/viewer-app.ts";
import {
  acceptInvitation,
  createAccountDeviceConnection,
  createDeviceConnection,
  deleteViewerPhoto,
  recordAlbumDownloadCompletion,
  requestInvitationCode,
} from "./viewer-api.ts";

const root = document.querySelector<HTMLElement>("#viewer-root");
if (!root) throw new Error("Viewer root is missing");

interface ManifestRequestState {
  readonly continueAfterPage: boolean;
  readonly expectedRevision: string;
  readonly refresh: boolean;
  readonly reject?: (error: Error) => void;
  readonly resolve?: () => void;
  readonly shareId: string;
}

interface AlbumDownloadPresentation extends AlbumDownloadProgress {
  readonly shareId: string;
}

interface ContributionPresentation extends ViewerContributionModel {
  readonly shareId: string;
}

interface DialogOpener {
  readonly action: "open-photo" | "open-video";
  readonly mediaId: string;
}

type PhotoNavigationAction = "next-photo" | "previous-photo";
const PHOTO_LONG_PRESS_DELAY = 500;
const PHOTO_LONG_PRESS_MOVE_TOLERANCE = 12;
const MAX_TIMEOUT_MS = 2_147_483_647;

interface PhotoLongPress {
  readonly mediaId: string;
  readonly pointerId: number;
  readonly timerId: number;
  readonly x: number;
  readonly y: number;
}

class FixtureHarness {
  #dialogOpener: DialogOpener | undefined;
  #model: ViewerUiModel;
  #photoLongPress: PhotoLongPress | undefined;
  #skipPhotoOpenId: string | undefined;

  constructor(
    private readonly app: ViewerApp,
    initialModel: ViewerUiModel,
  ) {
    this.#model = initialModel;
  }

  start(): void {
    root!.addEventListener("click", (event) => this.handleClick(event));
    root!.addEventListener("change", (event) => this.handleChange(event));
    root!.addEventListener("keydown", (event) => this.handlePhotoKeydown(event));
    root!.addEventListener("pointerdown", (event) => this.startPhotoLongPress(event));
    root!.addEventListener("pointermove", (event) => this.cancelPhotoLongPressForMovement(event));
    root!.addEventListener("pointerup", (event) => this.clearPhotoLongPress(event.pointerId));
    root!.addEventListener("contextmenu", (event) => this.preventPhotoContextMenu(event));
    root!.addEventListener("pointercancel", () => this.clearPhotoLongPress());
    root!.addEventListener(
      "aasaan:photo-position",
      (event) => this.handlePhotoPosition(event),
    );
    root!.addEventListener(
      "aasaan:fixture-transfer",
      (event) => this.handleTransferUpdate(event),
    );
    this.render();
    if (new URLSearchParams(location.search).get("fixture") === "webp-photo-dialog") {
      void this.loadWebpFixturePreview();
    }
  }

  private async loadWebpFixturePreview(): Promise<void> {
    const response = await fetch("/assets/fixture-unsplash.webp");
    const source = URL.createObjectURL(await response.blob());
    this.#model = {
      ...this.#model,
      shares: this.#model.shares.map((share) => (
        share.id !== "00000000-0000-4000-8000-000000000010"
          ? share
          : {
            ...share,
            media: share.media.map((media) => (
              media.id === "00000000-0000-4000-8000-000000000020"
                ? { ...media, aspectRatio: 1600 / 1067, source }
                : media
            )),
          }
      )),
    };
    this.render();
  }

  private handleClick(event: Event): void {
    const target = (event.target as Element).closest<HTMLElement>("[data-action]");
    if (!target) return;
    const action = target.dataset.action;
    if (action === "toggle-shares") {
      this.#model = { ...this.#model, shareMenuOpen: !this.#model.shareMenuOpen };
    } else if (action === "toggle-media-sort-direction") {
      this.#model = {
        ...this.#model,
        mediaSortDirection: this.#model.mediaSortDirection === "ascending"
          ? "descending"
          : "ascending",
      };
    } else if (action === "select-share" && target.dataset.shareId) {
      this.#model = {
        ...this.#model,
        selectedMediaId: undefined,
        selectedShareId: target.dataset.shareId,
        shareMenuOpen: false,
        photoSelectionActive: false,
        selectedPhotoIds: [],
      };
      this.#skipPhotoOpenId = undefined;
    } else if (
      action === "filter-collaborator"
      && target.dataset.collaboratorId
    ) {
      this.#model = {
        ...this.#model,
        selectedMediaId: undefined,
        selectedPhotoIds: [],
        shares: this.#model.shares.map((share) => (
          share.id === this.#model.selectedShareId
            ? {
                ...share,
                collaboratorFilter: target.dataset.collaboratorId!,
              }
            : share
        )),
      };
    } else if (action === "add-photos") {
      root!.querySelector<HTMLInputElement>(
        "input[data-contribution-input]",
      )?.click();
    } else if (action === "select-photos") {
      this.#model = {
        ...this.#model,
        photoSelectionActive: true,
        selectedPhotoIds: [],
      };
    } else if (action === "cancel-photo-selection") {
      this.#model = {
        ...this.#model,
        photoSelectionActive: false,
        selectedPhotoIds: [],
      };
      this.#skipPhotoOpenId = undefined;
    } else if (action === "toggle-photo-selection" && target.dataset.mediaId) {
      const selected = new Set(this.#model.selectedPhotoIds);
      if (selected.has(target.dataset.mediaId)) selected.delete(target.dataset.mediaId);
      else selected.add(target.dataset.mediaId);
      this.#model = { ...this.#model, selectedPhotoIds: [...selected] };
    } else if ((action === "open-photo" || action === "open-video") && target.dataset.mediaId) {
      if (action === "open-photo" && this.consumeSkippedPhotoOpen(target.dataset.mediaId)) {
        return;
      }
      this.openMedia(target);
      return;
    } else if (action === "close-dialog") {
      this.closeMediaDialog();
      return;
    } else if (action === "next-photo" || action === "previous-photo") {
      this.navigatePhoto(action);
      return;
    } else if (action === "download-video") {
      this.#model = {
        ...this.#model,
        transferProgress: 0.42,
        transferState: "transferring",
      };
      this.updateTransfer();
      return;
    } else if (action === "delete-photo" && target.dataset.mediaId) {
      if (!window.confirm("Delete this photo from the album? This can't be undone.")) {
        return;
      }
      this.#model = {
        ...this.#model,
        selectedMediaId: this.#model.selectedMediaId === target.dataset.mediaId
          ? undefined
          : this.#model.selectedMediaId,
        shares: this.#model.shares.map((share) => (
          share.id === this.#model.selectedShareId
            ? {
              ...share,
              media: share.media.filter((media) => media.id !== target.dataset.mediaId),
            }
            : share
        )),
      };
    } else if (action === "cancel-download") {
      this.#model = { ...this.#model, transferState: "cancelled" };
      this.updateTransfer();
      return;
    } else if (action === "download-all") {
      const total = this.#model.shares.find(
        (share) => share.id === this.#model.selectedShareId,
      )?.media.length ?? 0;
      this.#model = {
        ...this.#model,
        albumDownload: {
          completed: 0,
          current: 1,
          failed: 0,
          progress: 0.42,
          state: "downloading",
          total,
        },
      };
    } else if (action === "download-selected-photos") {
      const total = this.#model.selectedPhotoIds?.length ?? 0;
      if (total === 0) return;
      this.#model = {
        ...this.#model,
        albumDownload: {
          completed: 0,
          current: 1,
          failed: 0,
          progress: 0.42,
          state: "downloading",
          total,
        },
      };
    } else if (action === "load-more") {
      this.#model = {
        ...this.#model,
        shares: this.#model.shares.map((share) => (
          share.id === this.#model.selectedShareId
            ? { ...share, hasMore: false }
            : share
        )),
      };
    } else if (action === "cancel-album-download") {
      this.#model = {
        ...this.#model,
        albumDownload: this.#model.albumDownload
          ? {
            ...this.#model.albumDownload,
            progress: 0,
            state: "cancelled",
          }
          : undefined,
      };
    }
    this.render();
  }

  private handleChange(event: Event): void {
    const select = event.target;
    if (
      !(select instanceof HTMLSelectElement)
      || select.dataset.action !== "sort-media"
    ) {
      return;
    }
    this.#model = {
      ...this.#model,
      mediaSort: select.value === "created" ? "created" : "added",
    };
    this.render();
  }

  private openMedia(target: HTMLElement): void {
    const mediaId = target.dataset.mediaId;
    const action = target.dataset.action;
    if (
      !mediaId
      || (action !== "open-photo" && action !== "open-video")
    ) {
      return;
    }
    this.#dialogOpener = { action, mediaId };
    this.#model = { ...this.#model, selectedMediaId: mediaId };
    this.render();
  }

  private navigatePhoto(action: PhotoNavigationAction): void {
    const selected = this.#model.shares.find(
      (share) => share.id === this.#model.selectedShareId,
    );
    const photos = selected?.media.filter((media) => media.kind === "image") ?? [];
    const currentIndex = photos.findIndex((media) => media.id === this.#model.selectedMediaId);
    const direction = action === "next-photo" ? 1 : -1;
    const next = photos[currentIndex + direction];
    if (!next) return;
    this.#model = { ...this.#model, selectedMediaId: next.id };
    this.render();
  }

  private closeMediaDialog(): void {
    const dialog = root!.querySelector<HTMLDialogElement>("dialog");
    if (dialog?.open) {
      closeDialog(dialog);
      return;
    }
    this.handleDialogClosed();
  }

  private handlePhotoKeydown(event: KeyboardEvent): void {
    if (!root!.querySelector<HTMLDialogElement>('dialog[data-dialog="photo"]')?.open) return;
    const action = event.key === "ArrowRight"
      ? "next-photo"
      : event.key === "ArrowLeft"
        ? "previous-photo"
        : undefined;
    if (!action) return;
    event.preventDefault();
    this.navigatePhoto(action);
  }

  private handlePhotoPosition(event: Event): void {
    const mediaId = photoPositionMediaId(event);
    const selected = this.#model.shares.find(
      (share) => share.id === this.#model.selectedShareId,
    );
    if (
      !mediaId
      || mediaId === this.#model.selectedMediaId
      || !selected?.media.some((media) => media.id === mediaId && media.kind === "image")
    ) {
      return;
    }
    this.#model = { ...this.#model, selectedMediaId: mediaId };
    this.render();
  }

  private startPhotoLongPress(event: PointerEvent): void {
    if (!event.isPrimary || event.pointerType === "mouse") return;
    const target = photoTileFromEvent(event);
    const selected = this.#model.shares.find(
      (share) => share.id === this.#model.selectedShareId,
    );
    if (
      !target?.dataset.mediaId
      || selected?.access !== "download"
      || !selected.media.some((media) => (
        media.id === target.dataset.mediaId && media.kind === "image"
      ))
    ) {
      return;
    }
    this.clearPhotoLongPress();
    const mediaId = target.dataset.mediaId;
    this.#photoLongPress = {
      mediaId,
      pointerId: event.pointerId,
      timerId: window.setTimeout(() => this.selectPhotoFromLongPress(mediaId), PHOTO_LONG_PRESS_DELAY),
      x: event.clientX,
      y: event.clientY,
    };
  }

  private cancelPhotoLongPressForMovement(event: PointerEvent): void {
    const press = this.#photoLongPress;
    if (!press || press.pointerId !== event.pointerId) return;
    if (Math.hypot(event.clientX - press.x, event.clientY - press.y) > PHOTO_LONG_PRESS_MOVE_TOLERANCE) {
      this.clearPhotoLongPress();
    }
  }

  private clearPhotoLongPress(pointerId?: number): void {
    const press = this.#photoLongPress;
    if (!press || (pointerId !== undefined && press.pointerId !== pointerId)) return;
    window.clearTimeout(press.timerId);
    this.#photoLongPress = undefined;
  }

  private selectPhotoFromLongPress(mediaId: string): void {
    if (this.#photoLongPress?.mediaId !== mediaId) return;
    this.#photoLongPress = undefined;
    this.#skipPhotoOpenId = mediaId;
    const selectedPhotoIds = new Set(this.#model.selectedPhotoIds);
    selectedPhotoIds.add(mediaId);
    this.#model = {
      ...this.#model,
      photoSelectionActive: true,
      selectedPhotoIds: [...selectedPhotoIds],
    };
    this.render();
  }

  private consumeSkippedPhotoOpen(mediaId: string): boolean {
    if (this.#skipPhotoOpenId !== mediaId) return false;
    this.#skipPhotoOpenId = undefined;
    return true;
  }

  private preventPhotoContextMenu(event: Event): void {
    if (photoTileFromEvent(event)) event.preventDefault();
  }

  private handleTransferUpdate(event: Event): void {
    if (!(event instanceof CustomEvent) || !event.detail) return;
    const detail = event.detail as {
      progress?: number;
      state?: TransferState;
    };
    this.#model = {
      ...this.#model,
      transferProgress: detail.progress,
      transferState: detail.state,
    };
    this.updateTransfer();
  }

  private updateTransfer(): void {
    if (!this.app.updateTransfer(this.#model)) this.render();
  }

  private handleDialogClosed(): void {
    if (!this.#model.selectedMediaId) return;
    const opener = this.#dialogOpener;
    this.#dialogOpener = undefined;
    this.#model = { ...this.#model, selectedMediaId: undefined };
    this.render();
    restoreDialogFocus(opener);
  }

  private render(): void {
    this.app.render(this.#model);
    const dialog = root!.querySelector<HTMLDialogElement>("dialog");
    if (dialog) {
      openDialog(dialog, findDialogOpener(this.#dialogOpener));
      dialog.addEventListener("close", () => this.handleDialogClosed(), { once: true });
    }
  }
}

class LiveViewerController {
  readonly #availableShares = new Map<string, AvailableShare>();
  readonly #catalogue = new ShareCatalogue();
  readonly #grants = new Map<string, ShareGrant>();
  readonly #manifestRequests = new Map<string, ManifestRequestState>();
  readonly #mediaCache = new ViewerMediaCache();
  readonly #mediaRequests = new Set<string>();
  #connection: ViewerConnectionTransport;
  #albumDownload: AlbumDownloadPresentation | undefined;
  #albumDownloadQueue: AlbumDownloadQueue | undefined;
  #albumDownloadRenderPending = false;
  #activePreviewRequestId: string | null = null;
  #activeTransferMediaId: string | undefined;
  #activeTransferRequestId: string | null = null;
  #account: ViewerUiModel["account"];
  #admission: ViewerAdmissionState = "authorizing";
  #accountSignInUrl: string | undefined;
  #announcement = "";
  #connectionToken: string | null = null;
  #contribution: ContributionPresentation | undefined;
  #dialogOpener: DialogOpener | undefined;
  #destination: string | undefined;
  #grantExpiryTimer: number | null = null;
  #inviteToken: string | null = null;
  readonly #loadingMoreShareIds = new Set<string>();
  #mediaSort: ViewerUiModel["mediaSort"] = "added";
  #mediaSortDirection: ViewerUiModel["mediaSortDirection"] = "ascending";
  #mode: ViewerUiModel["mode"] = "gallery";
  #photoSelectionActive = false;
  #photoLongPress: PhotoLongPress | undefined;
  #requestedAccountShareId: string | undefined;
  #revokedShareLabel: string | undefined;
  readonly #selectedPhotoIds = new Set<string>();
  #skipPhotoOpenId: string | undefined;
  #selectedMediaId: string | undefined;
  #shareMenuOpen = false;
  #deletingMediaId: string | undefined;
  #transferProgress: number | undefined;
  #transferState: TransferState | undefined;
  readonly #uploaderFilters = new Map<string, string>();
  #videoCapability: VideoCapabilityState = "hidden";
  #verificationSubmitting = false;

  constructor(private readonly app: ViewerApp) {
    this.#connection = this.createConnection();
  }

  start(): void {
    void this.#mediaCache.purgeExpired();
    root!.addEventListener("click", (event) => void this.handleClick(event));
    root!.addEventListener("change", (event) => void this.handleChange(event));
    root!.addEventListener("keydown", (event) => this.handlePhotoKeydown(event));
    root!.addEventListener("pointerdown", (event) => this.startPhotoLongPress(event));
    root!.addEventListener("pointermove", (event) => this.cancelPhotoLongPressForMovement(event));
    root!.addEventListener("pointerup", (event) => this.clearPhotoLongPress(event.pointerId));
    root!.addEventListener("contextmenu", (event) => this.preventPhotoContextMenu(event));
    root!.addEventListener("pointercancel", () => this.clearPhotoLongPress());
    root!.addEventListener(
      "aasaan:photo-position",
      (event) => this.handlePhotoPosition(event),
    );
    root!.addEventListener("submit", (event) => void this.handleSubmit(event));
    window.addEventListener("popstate", () => this.restoreShareFromHistory());
    this.#inviteToken = invitationToken();
    if (this.#inviteToken) {
      void this.openKnownDeviceFromInvitation();
      return;
    }
    const savedAccountShareId = accountShareId();
    if (savedAccountShareId) {
      void this.openAccountShare(savedAccountShareId);
      return;
    }
    this.#admission = "unavailable";
    this.render();
  }

  private async openAccountShare(shareId: string): Promise<void> {
    this.#requestedAccountShareId = shareId;
    this.#mode = "gallery";
    this.#admission = "authorizing";
    this.render();
    try {
      const result = await createAccountDeviceConnection(shareId);
      if (result.status === "ready") {
        this.#account = result.account;
        this.#connectionToken = result.connectionToken;
        void this.#mediaCache.reconcileAuthorizedShares([]);
        this.#connection.close();
        this.#connection = this.createConnection();
        this.#connection.connect(result.connectionToken);
        return;
      }
      const returnTo = `/shared/${encodeURIComponent(shareId)}`;
      if (result.status === "sign-in-required") {
        window.location.replace(
          `/account?returnTo=${encodeURIComponent(returnTo)}`,
        );
        return;
      }
      if (result.status === "account-mismatch") {
        this.#destination =
          "This album was shared with a different AasaanShare account.";
        this.#accountSignInUrl =
          `/account?signOut=1&returnTo=${encodeURIComponent(returnTo)}`;
      } else {
        this.#destination = "This shared album is no longer available.";
      }
    } catch {
      this.#destination = "This shared album could not be opened.";
    }
    this.#admission = "unavailable";
    this.render();
  }

  private async openKnownDeviceFromInvitation(): Promise<void> {
    this.#mode = "gallery";
    this.#admission = "authorizing";
    this.render();
    try {
      const result = await createDeviceConnection(this.#inviteToken!);
      if (result.status === "ready") {
        this.#connectionToken = result.connectionToken;
        void this.#mediaCache.reconcileAuthorizedShares([]);
        this.#connection.close();
        this.#connection = this.createConnection();
        this.#connection.connect(result.connectionToken);
        return;
      }
      if (result.status === "verification-required") {
        this.#mode = "invitation";
        this.render();
        return;
      }
    } catch {
      // The unavailable state below keeps a copied or stale link fail-closed.
    }
    this.#destination =
      "This Person link is no longer available. Ask the sender to share their current link.";
    this.#admission = "unavailable";
    this.render();
  }

  private createConnection(): ViewerConnectionTransport {
    const events: ConnectionClientEvents = {
      onAdmission: (state) => {
        this.#admission = state;
        this.#announcement = state === "connected" ? "Share opened" : "";
        this.render();
      },
      onControl: (message) => this.handleControl(message),
      onGrants: (grants) => this.handleGrants(grants),
      onShares: (shares) => this.handleShares(shares),
      onTransfer: (update) => {
        if (this.#albumDownloadQueue?.updateProgress(update.requestId, update.progress)) {
          return;
        }
        if (update.requestId !== this.#activeTransferRequestId) return;
        this.#transferProgress = update.progress;
        this.#transferState = update.state;
        this.updateTransferPresentation(update.state);
      },
      onTransferMeasurement: (measurement) => {
        const effectiveBitrateBps = measurement.receiveDurationMs !== null
            && measurement.receiveDurationMs > 0
          ? Math.round(
              measurement.byteLength * 8 * 1000
                / measurement.receiveDurationMs,
            )
          : null;
        console.info("AASAAN_VIEWER_TRANSFER", JSON.stringify({
          ...measurement,
          effectiveBitrateBps,
        }));
      },
    };
    return new StorageViewerConnectionClient(events);
  }

  private async handleClick(event: Event): Promise<void> {
    const target = (event.target as Element).closest<HTMLElement>("[data-action]");
    if (!target) return;
    switch (target.dataset.action) {
      case "request-code":
        await this.requestCode();
        break;
      case "retry-connection":
        this.#connection.retry();
        break;
      case "toggle-shares":
        this.#shareMenuOpen = !this.#shareMenuOpen;
        this.render();
        break;
      case "select-share":
        if (target.dataset.shareId) this.selectShare(target.dataset.shareId, target);
        break;
      case "filter-collaborator":
        if (target.dataset.collaboratorId) {
          const selected = this.#catalogue.selected;
          if (selected) {
            this.#uploaderFilters.set(
              selected.shareId,
              target.dataset.collaboratorId,
            );
            this.#selectedMediaId = undefined;
            this.clearPhotoSelection();
            this.render();
            this.loadGalleryMedia(
              selected.shareId,
              this.visibleDescriptors(selected),
            );
            void this.loadRemainingFilterMedia(selected.shareId);
          }
        }
        break;
      case "toggle-media-sort-direction":
        this.#mediaSortDirection = this.#mediaSortDirection === "ascending"
          ? "descending"
          : "ascending";
        this.render();
        break;
      case "add-photos":
        root!.querySelector<HTMLInputElement>(
          "input[data-contribution-input]",
        )?.click();
        break;
      case "select-photos":
        if (this.#catalogue.selected?.access === "download") {
          this.#photoSelectionActive = true;
          this.#selectedPhotoIds.clear();
          this.render();
        }
        break;
      case "cancel-photo-selection":
        this.clearPhotoSelection();
        this.render();
        break;
      case "toggle-photo-selection":
        if (target.dataset.mediaId) this.togglePhotoSelection(target.dataset.mediaId);
        break;
      case "open-photo":
      case "open-video":
        if (
          target.dataset.mediaId
          && (target.dataset.action !== "open-photo"
            || !this.consumeSkippedPhotoOpen(target.dataset.mediaId))
        ) {
          this.openMedia(target.dataset.mediaId);
        }
        break;
      case "close-dialog":
        this.closeMediaDialog();
        break;
      case "next-photo":
      case "previous-photo":
        this.navigatePhoto(
          target.dataset.action === "next-photo" ? "next-photo" : "previous-photo",
        );
        break;
      case "download-photo":
        if (target.dataset.mediaId) await this.downloadPhoto(target.dataset.mediaId);
        break;
      case "delete-photo":
        if (target.dataset.mediaId) await this.deletePhoto(target.dataset.mediaId);
        break;
      case "download-all":
        await this.downloadAll();
        break;
      case "download-selected-photos":
        await this.downloadSelectedPhotos();
        break;
      case "load-more":
        this.loadMore();
        break;
      case "download-video":
        if (target.dataset.mediaId) await this.downloadVideo(target.dataset.mediaId);
        break;
      case "cancel-album-download":
        this.cancelAlbumDownload();
        break;
      case "cancel-download":
        if (this.#activeTransferRequestId) {
          this.#connection.media.cancel(this.#activeTransferRequestId);
        }
        break;
      case "focus-shares":
        root!.querySelector<HTMLElement>(".share-link, .switcher-button")?.focus();
        break;
    }
  }

  private async handleChange(event: Event): Promise<void> {
    const input = event.target;
    if (
      input instanceof HTMLSelectElement
      && input.dataset.action === "sort-media"
    ) {
      this.#mediaSort = input.value === "created" ? "created" : "added";
      this.render();
      return;
    }
    if (
      !(input instanceof HTMLInputElement)
      || !input.matches("input[data-contribution-input]")
    ) {
      return;
    }
    const files = [...(input.files ?? [])];
    input.value = "";
    if (files.length === 0) return;
    const share = this.#catalogue.selected;
    const grant = share ? this.#grants.get(share.shareId) : undefined;
    const connectionToken = this.#connectionToken;
    if (!share || !grant?.canContribute || !connectionToken) {
      this.#announcement = "Collaborative uploads are no longer available.";
      this.render();
      return;
    }
    const shareId = share.shareId;
    this.#contribution = {
      shareId,
      state: "preparing",
      totalItems: files.length,
    };
    this.render();
    try {
      const count = await uploadBrowserContribution({
        connectionToken,
        files,
        onProgress: (progress) => this.updateContributionProgress(
          shareId,
          progress,
        ),
        shareId,
      });
      const message = count === 1
        ? "1 item added to the album."
        : `${count} items added to the album.`;
      this.#contribution = {
        message,
        shareId,
        state: "completed",
        totalItems: count,
      };
      this.#announcement = message;
      this.#connection.retry();
    } catch (error) {
      const message = error instanceof Error && error.message
        ? error.message
        : "These items could not be added.";
      this.#contribution = {
        message,
        shareId,
        state: "failed",
        totalItems: files.length,
      };
      this.#announcement = message;
    }
    this.render();
  }

  private updateContributionProgress(
    shareId: string,
    progress: BrowserContributionProgress,
  ): void {
    if (this.#contribution?.shareId !== shareId) return;
    this.#contribution = { ...progress, shareId, state: progress.phase };
    this.render();
  }

  private async handleSubmit(event: Event): Promise<void> {
    const form = event.target as HTMLFormElement;
    if (form.dataset.form !== "verification") return;
    event.preventDefault();
    const code = String(new FormData(form).get("code") ?? "");
    if (!/^\d{6}$/.test(code) || !this.#inviteToken) return;
    this.#admission = "authorizing";
    this.#verificationSubmitting = true;
    this.render();
    try {
      await acceptInvitation(this.#inviteToken, code);
      this.#verificationSubmitting = false;
      await this.openKnownDeviceFromInvitation();
    } catch (error) {
      this.#verificationSubmitting = false;
      this.#mode = "verification";
      this.#announcement = error instanceof Error
        ? error.message
        : "This code could not be verified. Try again.";
      this.render();
    }
  }

  private async requestCode(): Promise<void> {
    if (!this.#inviteToken) return;
    this.#admission = "authorizing";
    this.#verificationSubmitting = true;
    this.render();
    try {
      const response = await requestInvitationCode(this.#inviteToken);
      this.#verificationSubmitting = false;
      this.#destination = `Code sent to ${response.emailHint}`;
      this.#mode = "verification";
      this.render();
      root!.querySelector<HTMLInputElement>("#verification-code")?.focus();
    } catch (error) {
      this.#verificationSubmitting = false;
      this.#mode = "invitation";
      const message = error instanceof Error && error.message
        ? error.message
        : "This Person link could not be used. Ask the sender to share their current link.";
      this.#announcement = message;
      this.#destination = message;
      this.render();
    }
  }

  private handleShares(shares: readonly AvailableShare[]): void {
    this.#availableShares.clear();
    for (const share of shares) this.#availableShares.set(share.shareId, share);
    const change = this.#catalogue.reconcile(shares, [...this.#grants.values()]);
    this.reconcilePersistentCache();
    if (
      this.#albumDownload
      && !this.#catalogue.get(this.#albumDownload.shareId)
    ) {
      this.cancelAlbumDownload("share-removed");
    }
    this.#videoCapability = this.selectedGrantVideoCapability();
    this.restoreShareFromHistory();
    if (change.removedSelected) this.#announcement = "A share was removed. Another share is open.";
    this.render();
    this.ensureSelectedManifest();
  }

  private handleGrants(grants: readonly ShareGrant[]): void {
    const previousVideo = [...this.#grants.values()].some(canDownloadVideo);
    const now = Date.now();
    const activeGrants = grants.filter((grant) => (
      Date.parse(grant.expiresAt) > now
    ));
    const nextIds = new Set(activeGrants.map((grant) => grant.shareId));
    this.#grants.clear();
    for (const grant of activeGrants) {
      this.#grants.set(grant.shareId, grant);
      this.#catalogue.updateGrant(grant);
    }
    for (const share of this.#catalogue.values()) {
      if (!nextIds.has(share.shareId)) {
        const selected = this.#catalogue.selectedShareId === share.shareId;
        this.#connection.media.cancelShare(share.shareId, "share-removed");
        this.#catalogue.remove(share.shareId, "revoked");
        void this.#mediaCache.deleteShare(share.shareId);
        if (selected) this.#revokedShareLabel = share.label;
      }
    }
    if (
      this.#albumDownload
      && this.#grants.get(this.#albumDownload.shareId)?.access !== "download"
    ) {
      this.cancelAlbumDownload("access-revoked");
    }
    const hasVideo = activeGrants.some(canDownloadVideo);
    if (previousVideo && !hasVideo) {
      this.cancelAlbumDownload("feature-disabled");
      this.#connection.media.cancelVideo("feature-disabled");
      this.#selectedMediaId = undefined;
      this.#announcement = "Video downloads are unavailable now. Your photos are still available.";
      this.#videoCapability = "disabled-during-session";
    } else {
      this.#videoCapability = this.selectedGrantVideoCapability();
    }
    this.scheduleGrantExpiry();
    this.render();
  }

  private reconcilePersistentCache(): void {
    void this.#mediaCache.reconcileAuthorizedShares([]);
  }

  private scheduleGrantExpiry(): void {
    if (this.#grantExpiryTimer !== null) {
      window.clearTimeout(this.#grantExpiryTimer);
      this.#grantExpiryTimer = null;
    }
    const nextExpiry = Math.min(
      ...[...this.#grants.values()].map((grant) => Date.parse(grant.expiresAt)),
    );
    if (!Number.isFinite(nextExpiry)) return;
    const delay = Math.min(
      MAX_TIMEOUT_MS,
      Math.max(0, nextExpiry - Date.now()),
    );
    this.#grantExpiryTimer = window.setTimeout(() => {
      this.#grantExpiryTimer = null;
      this.handleGrants([...this.#grants.values()]);
    }, delay);
  }

  private handleControl(message: OwnerMediaControlMessage): void {
    if (message.type === "share.updated") {
      const previousRevision = this.#catalogue.get(
        message.share.shareId,
      )?.revision;
      this.#availableShares.set(message.share.shareId, message.share);
      this.#catalogue.reconcile(
        [...this.#availableShares.values()],
        [...this.#grants.values()],
      );
      this.reconcilePersistentCache();
      this.render();
      if (previousRevision !== message.share.revision) this.ensureSelectedManifest();
    } else if (message.type === "share.removed") {
      void this.#mediaCache.deleteShare(message.shareId);
      if (this.#albumDownload?.shareId === message.shareId) {
        this.cancelAlbumDownload("share-removed");
      }
      if (message.reason === "capability-disabled") {
        this.#catalogue.get(message.shareId)?.gallery.removeVideos();
        this.#connection.media.cancelVideo("feature-disabled");
        this.#selectedMediaId = undefined;
        this.#videoCapability = "disabled-during-session";
        this.#announcement = "Video downloads are unavailable now. Your photos are still available.";
        this.render();
        return;
      }
      const removed = this.#catalogue.get(message.shareId);
      const change = this.#catalogue.remove(message.shareId, message.reason);
      this.#availableShares.delete(message.shareId);
      if (change.removedSelected) {
        this.#revokedShareLabel = removed?.label ?? "This share";
        this.#announcement = "This share is no longer available. Your other shares are still here.";
      }
      this.render(true);
    } else if (message.type === "manifest.page") {
      const request = this.#manifestRequests.get(message.requestId);
      this.#manifestRequests.delete(message.requestId);
      if (
        request?.shareId !== message.shareId
        || request.expectedRevision !== message.revision
      ) {
        request?.reject?.(new Error("Album details changed while loading."));
        return;
      }
      const share = this.#catalogue.get(message.shareId);
      const grant = this.#grants.get(message.shareId);
      if (!share || !grant || share.revision !== message.revision) {
        request.reject?.(new Error("Album is unavailable."));
        return;
      }
      try {
        visibleMediaForGrant(message.media, grant);
        const committed = share.gallery.applyPage(message);
        const selected = this.#catalogue.selectedShareId === message.shareId;
        if (committed) {
          this.render();
        }
        if (selected && committed) {
          const filter = this.#uploaderFilters.get(message.shareId) ?? "all";
          const descriptors = request.refresh
            ? share.gallery.items().map((item) => item.descriptor)
            : message.media;
          this.loadGalleryMedia(
            message.shareId,
            descriptors.filter((descriptor) => (
              descriptorMatchesUploaderFilter(descriptor, filter)
            )),
          );
          if (
            filter !== "all"
            && message.nextCursor !== null
            && !this.#loadingMoreShareIds.has(message.shareId)
          ) {
            void this.loadRemainingFilterMedia(message.shareId);
          }
        }
        if (selected && request.continueAfterPage && message.nextCursor !== null) {
          this.requestManifest(message.shareId, { refresh: true });
        }
        request.resolve?.();
      } catch {
        request.reject?.(new Error("This album could not be opened."));
        this.#announcement = "This share could not be opened.";
        this.render();
      }
    } else if (message.type === "media.error") {
      const request = this.#manifestRequests.get(message.requestId);
      this.#manifestRequests.delete(message.requestId);
      request?.reject?.(new Error("Album details could not be loaded."));
    }
  }

  private requestManifest(
    shareId: string,
    options: { readonly fresh?: boolean; readonly refresh?: boolean } = {},
  ): void {
    void this.requestManifestPage(shareId, options).catch(() => undefined);
  }

  private ensureSelectedManifest(): void {
    const selected = this.#catalogue.selected;
    if (!selected) return;
    if (
      selected.gallery.revision !== null
      && selected.gallery.revision !== selected.revision
    ) {
      this.requestManifest(selected.shareId, { fresh: true, refresh: true });
      return;
    }
    if (selected.gallery.items().length === 0) {
      this.requestManifest(selected.shareId);
    }
  }

  private loadMore(): void {
    const selected = this.#catalogue.selected;
    if (
      !selected
      || !selected.gallery.nextCursor
      || this.#loadingMoreShareIds.has(selected.shareId)
    ) {
      return;
    }
    this.#loadingMoreShareIds.add(selected.shareId);
    this.render();
    void this.requestManifestPage(selected.shareId).catch(() => {
      this.#announcement = "More photos could not be loaded. Try again.";
    }).finally(() => {
      this.#loadingMoreShareIds.delete(selected.shareId);
      this.render();
    });
  }

  private async loadRemainingFilterMedia(shareId: string): Promise<void> {
    const filter = this.#uploaderFilters.get(shareId) ?? "all";
    if (filter === "all" || this.#loadingMoreShareIds.has(shareId)) return;
    this.#loadingMoreShareIds.add(shareId);
    this.render();
    try {
      while (
        this.#catalogue.selectedShareId === shareId
        && (this.#uploaderFilters.get(shareId) ?? "all") === filter
      ) {
        const share = this.#catalogue.get(shareId);
        if (!share?.gallery.hasMore) return;
        await this.requestManifestPage(shareId, {
          autoContinue: false,
          refresh: true,
        });
      }
    } catch {
      if (this.#catalogue.selectedShareId === shareId) {
        this.#announcement = "Some collaborator photos could not be loaded. Try again.";
      }
    } finally {
      this.#loadingMoreShareIds.delete(shareId);
      this.render();
      const currentFilter = this.#uploaderFilters.get(shareId) ?? "all";
      if (
        this.#catalogue.selectedShareId === shareId
        && currentFilter !== "all"
        && currentFilter !== filter
        && this.#catalogue.get(shareId)?.gallery.hasMore
      ) {
        void this.loadRemainingFilterMedia(shareId);
      }
    }
  }

  private requestManifestPage(
    shareId: string,
    options: {
      readonly autoContinue?: boolean;
      readonly fresh?: boolean;
      readonly refresh?: boolean;
    } = {},
  ): Promise<void> {
    const share = this.#catalogue.get(shareId);
    if (!share) return Promise.reject(new Error("Album is unavailable."));
    if (!options.fresh && !options.refresh && !share.gallery.hasMore) {
      return Promise.resolve();
    }
    const requestId = crypto.randomUUID().toLowerCase();
    return new Promise((resolve, reject) => {
      this.#manifestRequests.set(requestId, {
        continueAfterPage: options.refresh === true && options.autoContinue !== false,
        expectedRevision: share.revision,
        refresh: options.refresh === true,
        reject,
        resolve,
        shareId,
      });
      if (!this.#connection.requestManifest({
        after: options.fresh ? null : share.gallery.nextCursor,
        ...(options.refresh ? { limit: 100 } : {}),
        requestId,
        revision: options.fresh ? null : share.revision,
        shareId,
      })) {
        this.#manifestRequests.delete(requestId);
        reject(new Error("Album details could not be requested."));
      }
    });
  }

  private loadGalleryMedia(
    shareId: string,
    descriptors: readonly MediaDescriptor[],
  ): void {
    if (this.#catalogue.selectedShareId !== shareId) return;
    const share = this.#catalogue.get(shareId);
    if (!share) return;
    const requests: Promise<void>[] = [];
    for (const descriptor of descriptors) {
      const rendition = galleryRendition(descriptor);
      const existing = share.gallery.items().find((item) => (
        item.descriptor.mediaId === descriptor.mediaId
      ));
      if (
        rendition === "preview"
          ? existing?.previewUrl
          : existing?.thumbnailUrl
      ) {
        continue;
      }
      const requestKey = `${shareId}:${descriptor.mediaId}:${rendition}`;
      if (this.#mediaRequests.has(requestKey)) continue;
      this.#mediaRequests.add(requestKey);
      const requestId = crypto.randomUUID().toLowerCase();
      requests.push(this.loadGalleryItem(
        share,
        descriptor,
        rendition,
        requestId,
      ).catch(() => {
        this.#announcement = "A gallery item could not be loaded.";
        this.render();
      }).finally(() => this.#mediaRequests.delete(requestKey)));
    }
    void Promise.all(requests);
  }

  private async loadGalleryItem(
    share: ViewerShare,
    descriptor: MediaDescriptor,
    rendition: RenditionName,
    requestId: string,
  ): Promise<void> {
    const initialGrant = this.currentMediaGrant(share, descriptor);
    if (!initialGrant) return;
    const cacheRequest = {
      descriptor,
      expiresAt: initialGrant.expiresAt,
      rendition,
      revision: share.revision,
      shareId: share.shareId,
    };
    if (!this.currentMediaGrant(share, descriptor)) return;
    const result = await this.#connection.media.request({
      descriptor,
      rendition,
      requestId,
      shareId: share.shareId,
    });
    const currentGrant = this.currentMediaGrant(share, descriptor);
    if (!result.blob || !currentGrant) return;
    share.gallery.setObjectUrl(descriptor.mediaId, rendition, result.blob);
    this.render();
  }

  private currentMediaGrant(
    share: ViewerShare,
    descriptor: MediaDescriptor,
  ): ShareGrant | undefined {
    const grant = this.#grants.get(share.shareId);
    if (
      this.#catalogue.get(share.shareId) !== share
      || share.gallery.descriptor(descriptor.mediaId) !== descriptor
      || !grant
      || Date.parse(grant.expiresAt) <= Date.now()
    ) {
      return undefined;
    }
    return grant;
  }

  private selectShare(shareId: string, target: HTMLElement): void {
    const current = this.#catalogue.selected;
    if (current) current.gallery.scrollTop = window.scrollY;
    if (this.#activePreviewRequestId) {
      this.#connection.media.cancel(this.#activePreviewRequestId);
      this.#activePreviewRequestId = null;
    }
    this.#catalogue.select(shareId);
    this.#shareMenuOpen = false;
    this.#selectedMediaId = undefined;
    this.clearPhotoSelection();
    this.#revokedShareLabel = undefined;
    this.#videoCapability = this.selectedGrantVideoCapability();
    history.pushState({}, "", selectedShareLocation(location.href, shareId));
    this.render();
    requestAnimationFrame(() => {
      window.scrollTo({ top: this.#catalogue.selected?.gallery.scrollTop ?? 0 });
      root!.querySelector<HTMLElement>(
        `[data-share-id="${CSS.escape(shareId)}"]`,
      )?.focus();
    });
    this.ensureSelectedManifest();
    target.blur();
  }

  private restoreShareFromHistory(): void {
    const shareId = new URLSearchParams(location.search).get("share")
      ?? this.#requestedAccountShareId;
    if (shareId && this.#catalogue.get(shareId)) this.#catalogue.select(shareId);
  }

  private openMedia(mediaId: string): void {
    const share = this.#catalogue.selected;
    const descriptor = share?.gallery.descriptor(mediaId);
    if (!share || !descriptor) return;
    this.#dialogOpener = {
      action: descriptor.kind === "video" ? "open-video" : "open-photo",
      mediaId,
    };
    this.#selectedMediaId = mediaId;
    this.render();
    if (descriptor.kind === "image" && descriptor.renditions.preview) {
      const current = share.gallery.items().find((item) => item.descriptor.mediaId === mediaId);
      if (!current?.previewUrl) this.loadPreview(share.shareId, descriptor);
    }
  }

  private loadPreview(shareId: string, descriptor: MediaDescriptor): void {
    void this.requestPreview(shareId, descriptor, true);
  }

  private requestPreview(
    shareId: string,
    descriptor: MediaDescriptor,
    reportError: boolean,
  ): Promise<void> | undefined {
    if (descriptor.kind !== "image" || !descriptor.renditions.preview) return undefined;
    const requestKey = `${shareId}:${descriptor.mediaId}:preview`;
    if (this.#mediaRequests.has(requestKey)) return undefined;
    const requestId = crypto.randomUUID().toLowerCase();
    if (reportError) this.#activePreviewRequestId = requestId;
    this.#mediaRequests.add(requestKey);
    return this.#connection.media.request({
      descriptor,
      rendition: "preview",
      requestId,
      shareId,
    }).then((result) => {
      const share = this.#catalogue.get(shareId);
      if (result.blob && share) {
        share.gallery.setObjectUrl(descriptor.mediaId, "preview", result.blob);
        this.render();
      }
    }).catch((error) => {
      if (
        reportError
        && !(error instanceof DOMException && error.name === "AbortError")
      ) {
        this.#announcement = "This photo could not be opened.";
        this.render();
      }
    }).finally(() => {
      this.#mediaRequests.delete(requestKey);
      if (reportError && this.#activePreviewRequestId === requestId) {
        this.#activePreviewRequestId = null;
      }
    });
  }

  private closeMediaDialog(): void {
    const dialog = root!.querySelector<HTMLDialogElement>("dialog");
    if (dialog?.open) {
      closeDialog(dialog);
      return;
    }
    this.handleMediaDialogClosed();
  }

  private navigatePhoto(action: PhotoNavigationAction): void {
    const share = this.#catalogue.selected;
    const photos = share
      ? this.visibleDescriptors(share).filter((descriptor) => (
          descriptor.kind === "image"
        ))
      : [];
    const currentIndex = photos.findIndex(
      (descriptor) => descriptor.mediaId === this.#selectedMediaId,
    );
    const direction = action === "next-photo" ? 1 : -1;
    const next = photos[currentIndex + direction];
    if (!next) return;
    this.setSelectedPhoto(next.mediaId);
  }

  private visibleDescriptors(share: ViewerShare): readonly MediaDescriptor[] {
    const filter = this.#uploaderFilters.get(share.shareId) ?? "all";
    const visible = share.gallery.items()
      .map((item) => item.descriptor)
      .filter((descriptor) => descriptorMatchesUploaderFilter(descriptor, filter));
    if (this.#mediaSort === "added") {
      return this.#mediaSortDirection === "ascending" ? visible : [...visible].reverse();
    }
    return sortMediaDescriptorsByCreation(visible, this.#mediaSortDirection);
  }

  private handlePhotoPosition(event: Event): void {
    const mediaId = photoPositionMediaId(event);
    if (mediaId) this.setSelectedPhoto(mediaId);
  }

  private setSelectedPhoto(mediaId: string): void {
    const share = this.#catalogue.selected;
    const item = share?.gallery.items().find(
      (candidate) => candidate.descriptor.mediaId === mediaId,
    );
    const descriptor = item?.descriptor;
    if (
      !share
      || !item
      || descriptor?.kind !== "image"
      || mediaId === this.#selectedMediaId
    ) {
      return;
    }
    if (this.#activePreviewRequestId) {
      this.#connection.media.cancel(this.#activePreviewRequestId);
      this.#activePreviewRequestId = null;
    }
    this.#selectedMediaId = mediaId;
    this.render();
    if (descriptor.renditions.preview && !item.previewUrl) {
      this.loadPreview(share.shareId, descriptor);
    }
  }

  private handlePhotoKeydown(event: KeyboardEvent): void {
    if (!root!.querySelector<HTMLDialogElement>('dialog[data-dialog="photo"]')?.open) return;
    const action = event.key === "ArrowRight"
      ? "next-photo"
      : event.key === "ArrowLeft"
        ? "previous-photo"
        : undefined;
    if (!action) return;
    event.preventDefault();
    this.navigatePhoto(action);
  }

  private startPhotoLongPress(event: PointerEvent): void {
    if (!event.isPrimary || event.pointerType === "mouse") return;
    const target = photoTileFromEvent(event);
    const mediaId = target?.dataset.mediaId;
    const descriptor = mediaId
      ? this.#catalogue.selected?.gallery.descriptor(mediaId)
      : undefined;
    if (
      !mediaId
      || this.#catalogue.selected?.access !== "download"
      || descriptor?.kind !== "image"
    ) {
      return;
    }
    this.clearPhotoLongPress();
    this.#photoLongPress = {
      mediaId,
      pointerId: event.pointerId,
      timerId: window.setTimeout(() => this.selectPhotoFromLongPress(mediaId), PHOTO_LONG_PRESS_DELAY),
      x: event.clientX,
      y: event.clientY,
    };
  }

  private cancelPhotoLongPressForMovement(event: PointerEvent): void {
    const press = this.#photoLongPress;
    if (!press || press.pointerId !== event.pointerId) return;
    if (Math.hypot(event.clientX - press.x, event.clientY - press.y) > PHOTO_LONG_PRESS_MOVE_TOLERANCE) {
      this.clearPhotoLongPress();
    }
  }

  private clearPhotoLongPress(pointerId?: number): void {
    const press = this.#photoLongPress;
    if (!press || (pointerId !== undefined && press.pointerId !== pointerId)) return;
    window.clearTimeout(press.timerId);
    this.#photoLongPress = undefined;
  }

  private selectPhotoFromLongPress(mediaId: string): void {
    if (this.#photoLongPress?.mediaId !== mediaId) return;
    const descriptor = this.#catalogue.selected?.gallery.descriptor(mediaId);
    if (
      this.#catalogue.selected?.access !== "download"
      || descriptor?.kind !== "image"
    ) {
      return;
    }
    this.#photoLongPress = undefined;
    this.#skipPhotoOpenId = mediaId;
    this.#photoSelectionActive = true;
    this.#selectedPhotoIds.add(mediaId);
    this.render();
  }

  private consumeSkippedPhotoOpen(mediaId: string): boolean {
    if (this.#skipPhotoOpenId !== mediaId) return false;
    this.#skipPhotoOpenId = undefined;
    return true;
  }

  private preventPhotoContextMenu(event: Event): void {
    if (photoTileFromEvent(event)) event.preventDefault();
  }

  private handleMediaDialogClosed(): void {
    if (!this.#selectedMediaId) return;
    const opener = this.#dialogOpener;
    if (this.#activePreviewRequestId) {
      this.#connection.media.cancel(this.#activePreviewRequestId);
      this.#activePreviewRequestId = null;
    }
    this.#dialogOpener = undefined;
    this.#selectedMediaId = undefined;
    this.#activeTransferMediaId = undefined;
    this.#transferState = undefined;
    this.#transferProgress = undefined;
    this.render();
    restoreDialogFocus(opener);
  }

  private async downloadAll(): Promise<void> {
    const selected = this.#catalogue.selected;
    if (
      !selected
      || selected.access !== "download"
      || this.isAlbumDownloadActive()
      || this.#activeTransferRequestId
    ) {
      return;
    }

    this.#albumDownload = {
      completed: 0,
      current: 0,
      failed: 0,
      progress: 0,
      shareId: selected.shareId,
      state: "preparing",
      total: selected.gallery.items().length,
    };
    this.render();
    try {
      await this.loadAllAlbumMedia(selected.shareId);
    } catch {
      const albumDownload = this.#albumDownload;
      if (albumDownload && albumDownload.state !== "cancelled") {
        this.#albumDownload = {
          ...albumDownload,
          failed: 1,
          state: "failed",
        };
        this.#announcement = "This album could not be prepared for download.";
        this.render();
      }
      return;
    }

    if (this.#albumDownload?.state === "cancelled") return;
    const share = this.#catalogue.get(selected.shareId);
    const grant = share ? this.#grants.get(share.shareId) : undefined;
    if (!share || share.access !== "download") {
      this.cancelAlbumDownload("share-removed");
      return;
    }
    const availableDescriptors = share.gallery.items()
      .map((item) => item.descriptor)
      .filter((descriptor) => descriptor.renditions.original !== undefined)
      .filter((descriptor) => descriptor.kind === "image" || canDownloadVideo(grant));
    if (availableDescriptors.length === 0) {
      this.#albumDownload = {
        ...this.#albumDownload,
        state: "completed",
        total: 0,
      };
      this.#announcement = "No originals are available to download.";
      this.render();
      return;
    }
    this.#albumDownload = {
      ...this.#albumDownload,
      total: availableDescriptors.length,
    };

    await this.downloadDescriptors(
      selected.shareId,
      availableDescriptors,
      this.#connectionToken ?? undefined,
    );
  }

  private async downloadSelectedPhotos(): Promise<void> {
    const selected = this.#catalogue.selected;
    if (
      !selected
      || selected.access !== "download"
      || this.isAlbumDownloadActive()
      || this.#activeTransferRequestId
      || this.#selectedPhotoIds.size === 0
    ) {
      return;
    }
    const descriptors = selected.gallery.items()
      .map((item) => item.descriptor)
      .filter((descriptor) => (
        descriptor.kind === "image"
        && descriptor.renditions.original !== undefined
        && this.#selectedPhotoIds.has(descriptor.mediaId)
      ));
    if (descriptors.length === 0) return;
    this.#albumDownload = {
      completed: 0,
      current: 0,
      failed: 0,
      progress: 0,
      shareId: selected.shareId,
      state: "preparing",
      total: descriptors.length,
    };
    this.render();
    await this.downloadDescriptors(selected.shareId, descriptors);
  }

  private async downloadDescriptors(
    shareId: string,
    descriptors: readonly MediaDescriptor[],
    completionToken?: string,
  ): Promise<void> {
    let queue: AlbumDownloadQueue;
    queue = new AlbumDownloadQueue({
      cancelRequest: (requestId) => this.#connection.media.cancel(requestId),
      descriptors,
      onChange: (progress) => this.handleAlbumDownloadProgress(shareId, queue, progress),
      requestOriginal: (input) => {
        const currentShare = this.#catalogue.get(shareId);
        const currentGrant = this.#grants.get(shareId);
        if (
          currentShare?.access !== "download"
          || (input.descriptor.kind === "video" && !canDownloadVideo(currentGrant))
        ) {
          return Promise.reject(new Error("Download access is no longer available."));
        }
        return this.#connection.media.request(input);
      },
      save: saveVerifiedBlob,
      shareId,
    });
    this.#albumDownloadQueue = queue;
    const result = await queue.run();
    if (this.#albumDownloadQueue !== queue) return;
    this.#albumDownloadQueue = undefined;
    if (result.state === "completed" && completionToken) {
      await recordAlbumDownloadCompletion(shareId, completionToken)
        .catch(() => undefined);
    }
    this.#announcement = albumDownloadAnnouncement(result);
    this.render();
  }

  private async loadAllAlbumMedia(shareId: string): Promise<void> {
    while (
      this.#albumDownload?.shareId === shareId
      && this.#albumDownload.state === "preparing"
    ) {
      const share = this.#catalogue.get(shareId);
      if (!share?.gallery.hasMore) return;
      await this.requestManifestPage(shareId, {
        autoContinue: false,
        refresh: true,
      });
    }
  }

  private cancelAlbumDownload(reason = "viewer-request"): void {
    if (!this.isAlbumDownloadActive()) return;
    this.#albumDownloadQueue?.cancel();
    if (this.#albumDownload) {
      this.#albumDownload = {
        ...this.#albumDownload,
        progress: 0,
        state: "cancelled",
      };
    }
    if (reason === "viewer-request") {
      this.#announcement = "Album download cancelled.";
    }
    this.render();
  }

  private handleAlbumDownloadProgress(
    shareId: string,
    queue: AlbumDownloadQueue,
    progress: AlbumDownloadProgress,
  ): void {
    if (this.#albumDownloadQueue !== queue) return;
    this.#albumDownload = { ...progress, shareId };
    if (progress.state === "preparing" || progress.state === "completed" || progress.state === "cancelled") {
      this.render();
      return;
    }
    if (this.#albumDownloadRenderPending) return;
    this.#albumDownloadRenderPending = true;
    requestAnimationFrame(() => {
      this.#albumDownloadRenderPending = false;
      this.render();
    });
  }

  private isAlbumDownloadActive(): boolean {
    return this.#albumDownload?.state === "preparing"
      || this.#albumDownload?.state === "downloading";
  }

  private togglePhotoSelection(mediaId: string): void {
    const selected = this.#catalogue.selected;
    const descriptor = selected?.gallery.descriptor(mediaId);
    if (
      !this.#photoSelectionActive
      || selected?.access !== "download"
      || descriptor?.kind !== "image"
    ) {
      return;
    }
    if (this.#selectedPhotoIds.has(mediaId)) this.#selectedPhotoIds.delete(mediaId);
    else this.#selectedPhotoIds.add(mediaId);
    this.render();
  }

  private clearPhotoSelection(): void {
    this.#photoSelectionActive = false;
    this.#selectedPhotoIds.clear();
    this.#skipPhotoOpenId = undefined;
  }

  private async downloadPhoto(mediaId: string): Promise<void> {
    const share = this.#catalogue.selected;
    const descriptor = share?.gallery.descriptor(mediaId);
    if (
      !share
      || descriptor?.kind !== "image"
      || !descriptor.renditions.original
      || this.#activeTransferRequestId
      || this.isAlbumDownloadActive()
    ) return;
    const requestId = crypto.randomUUID().toLowerCase();
    const rendition = descriptor.renditions.original;
    this.#activeTransferRequestId = requestId;
    this.#activeTransferMediaId = mediaId;
    this.#transferState = "starting";
    this.#transferProgress = 0;
    this.updateTransferPresentation("starting");
    try {
      const result = await this.#connection.media.request({
        descriptor,
        rendition: "original",
        requestId,
        shareId: share.shareId,
        sink: new MemoryDownloadSink(rendition.mimeType, rendition.byteLength),
      });
      if (result.blob) {
        saveVerifiedBlob(
          result.blob,
          safeDownloadFilename(descriptor.mediaId, result.mimeType),
        );
      }
      this.#transferProgress = 1;
      this.#transferState = "completed";
    } catch (error) {
      if (!(error instanceof DOMException && error.name === "AbortError")) {
        this.#transferState = "failed";
      }
    } finally {
      this.#activeTransferRequestId = null;
      if (this.#transferState === "completed") this.render();
      else this.updateTransferPresentation(this.#transferState ?? "failed");
    }
  }

  private async deletePhoto(mediaId: string): Promise<void> {
    const share = this.#catalogue.selected;
    const descriptor = share?.gallery.descriptor(mediaId);
    const connectionToken = this.#connectionToken;
    if (
      !share
      || descriptor?.kind !== "image"
      || descriptor.uploadedBy?.recipientId !== share.viewerRecipientId
      || !connectionToken
      || this.#deletingMediaId
    ) {
      return;
    }
    if (!window.confirm("Delete this photo from the album? This can't be undone.")) {
      return;
    }

    this.#deletingMediaId = mediaId;
    this.render();
    try {
      await deleteViewerPhoto(share.shareId, mediaId, connectionToken);
      this.#announcement = "Photo deleted.";
      if (this.#selectedMediaId === mediaId) this.closeMediaDialog();
      this.#connection.retry();
    } catch (error) {
      this.#announcement = error instanceof Error
        ? error.message
        : "This photo could not be deleted.";
    } finally {
      this.#deletingMediaId = undefined;
      this.render();
    }
  }

  private async downloadVideo(mediaId: string): Promise<void> {
    const share = this.#catalogue.selected;
    const descriptor = share?.gallery.descriptor(mediaId);
    const grant = share ? this.#grants.get(share.shareId) : undefined;
    if (
      !share
      || descriptor?.kind !== "video"
      || !canDownloadVideo(grant)
      || this.#activeTransferRequestId
      || this.isAlbumDownloadActive()
    ) {
      return;
    }
    const requestId = crypto.randomUUID().toLowerCase();
    this.#activeTransferRequestId = requestId;
    this.#activeTransferMediaId = mediaId;
    this.#transferState = "starting";
    this.#transferProgress = 0;
    this.updateTransferPresentation("starting");
    try {
      const filename = safeDownloadFilename(
        descriptor.mediaId,
        descriptor.renditions.original.mimeType,
      );
      const sink = await createDownloadSink({
        mimeType: descriptor.renditions.original.mimeType,
        safeFilename: filename,
      });
      const result = await this.#connection.media.request({
        descriptor,
        rendition: "original",
        requestId,
        shareId: share.shareId,
        sink,
      });
      if (result.blob) saveVerifiedBlob(result.blob, filename);
      this.#transferProgress = 1;
      this.#transferState = "completed";
    } catch (error) {
      if (!(error instanceof DOMException && error.name === "AbortError")) {
        this.#transferState = "failed";
      }
    } finally {
      this.#activeTransferRequestId = null;
      if (this.#transferState === "completed") this.render();
      else this.updateTransferPresentation(this.#transferState ?? "failed");
    }
  }

  private selectedGrantVideoCapability(): VideoCapabilityState {
    const selectedId = this.#catalogue.selectedShareId;
    return selectedId && canDownloadVideo(this.#grants.get(selectedId))
      ? "testing"
      : "hidden";
  }

  private render(focusShare = false): void {
    this.app.render(this.model());
    if (this.#selectedMediaId) {
      const dialog = root!.querySelector<HTMLDialogElement>("dialog");
      if (dialog && !dialog.open) {
        openDialog(dialog, findDialogOpener(this.#dialogOpener));
        dialog.addEventListener("close", () => this.handleMediaDialogClosed(), { once: true });
      }
    }
    if (focusShare) {
      requestAnimationFrame(() => {
        root!.querySelector<HTMLElement>(".share-link, .switcher-button")?.focus();
      });
    }
  }

  private updateTransferPresentation(state: TransferState): void {
    if (!this.app.updateTransfer(this.model())) this.render();
    if (state === "cancelled" || state === "failed") {
      requestAnimationFrame(() => {
        const selectedMediaId = this.#selectedMediaId;
        const action = selectedMediaId === this.#activeTransferMediaId
          && selectedMediaId !== undefined
          && this.#catalogue.selected?.gallery.descriptor(selectedMediaId)?.kind === "image"
          ? "download-photo"
          : "download-video";
        root!.querySelector<HTMLElement>(`[data-action='${action}']`)?.focus();
      });
    }
  }

  private model(): ViewerUiModel {
    const shares = this.#catalogue.values().map((share): ViewerShareModel => {
      const collaboratorAvatarById = new Map(
        share.collaborators?.flatMap((collaborator) =>
          collaborator.avatarUrl
            ? [[collaborator.recipientId, collaborator.avatarUrl] as const]
            : []) ?? [],
      );
      const media = share.gallery.items()
        .filter((item) => item.descriptor.kind === "image" || share.capabilities.videoDownloadV1)
        .map((item): ViewerMediaModel => {
          const descriptor = item.descriptor;
          return {
            aspectRatio: descriptor.width / descriptor.height,
            canDelete: descriptor.kind === "image"
              && descriptor.uploadedBy?.recipientId === share.viewerRecipientId,
            canDownloadOriginal: descriptor.renditions.original !== undefined,
            createdAt: descriptor.createdAt,
            deleting: descriptor.mediaId === this.#deletingMediaId,
            durationLabel: descriptor.kind === "video"
              ? formatDuration(descriptor.durationMs)
              : undefined,
            id: descriptor.mediaId,
            kind: descriptor.kind,
            label: descriptor.kind === "video" ? "Original video" : "Shared photo",
            originalSizeLabel: descriptor.kind === "image" && descriptor.renditions.original
              ? formatBytes(descriptor.renditions.original.byteLength)
              : undefined,
            sizeLabel: descriptor.kind === "video"
              ? formatBytes(descriptor.originalByteLength)
              : undefined,
            source: item.previewUrl ?? item.thumbnailUrl,
            uploadedBy: descriptor.uploadedBy
              ? {
                ...descriptor.uploadedBy,
                ...(collaboratorAvatarById.get(descriptor.uploadedBy.recipientId)
                  ? {
                    avatarUrl: collaboratorAvatarById.get(
                      descriptor.uploadedBy.recipientId,
                    ),
                  }
                  : {}),
              }
              : undefined,
          };
        });
      const collaboratorsById = new Map<string, {
        count: number;
        displayName: string;
        recipientId: string;
      }>();
      let originalMediaCount = 0;
      for (const item of media) {
        if (!item.uploadedBy) {
          originalMediaCount += 1;
          continue;
        }
        const current = collaboratorsById.get(item.uploadedBy.recipientId);
        collaboratorsById.set(item.uploadedBy.recipientId, {
          count: (current?.count ?? 0) + 1,
          displayName: item.uploadedBy.displayName,
          recipientId: item.uploadedBy.recipientId,
        });
      }
      const collaborators = share.collaborators?.map((collaborator) => ({
        avatarUrl: collaborator.avatarUrl,
        count: collaborator.mediaCount,
        displayName: collaborator.displayName,
        recipientId: collaborator.recipientId,
      })) ?? [...collaboratorsById.values()]
        .sort((left, right) => left.displayName.localeCompare(right.displayName));
      const availableCollaboratorIds = new Set(
        collaborators.map((collaborator) => collaborator.recipientId),
      );
      const fullOriginalMediaCount = share.originalMediaCount ?? originalMediaCount;
      const summaryMediaCount = fullOriginalMediaCount + collaborators.reduce(
        (total, collaborator) => total + collaborator.count,
        0,
      );
      const requestedFilter = this.#uploaderFilters.get(share.shareId) ?? "all";
      const collaboratorFilter = requestedFilter === "all"
        || (requestedFilter === "original" && fullOriginalMediaCount > 0)
        || availableCollaboratorIds.has(requestedFilter)
        ? requestedFilter
        : "all";
      return {
        access: share.access,
        canContribute: share.canContribute,
        collaboratorFilter,
        collaborators,
        hasMore: share.gallery.nextCursor !== null,
        id: share.shareId,
        label: share.label,
        loadingMore: this.#loadingMoreShareIds.has(share.shareId),
        media,
        originalMediaCount: fullOriginalMediaCount,
        ownerAvatarUrl: share.ownerAvatarUrl,
        ownerEmail: share.ownerEmail,
        ownerUsername: share.ownerUsername,
        totalMediaCount: share.gallery.totalMediaCount ?? summaryMediaCount,
      };
    });
    const selectedShare = this.#catalogue.selected;
    const selectedPhotoIds = selectedShare
      ? [...this.#selectedPhotoIds].filter((mediaId) => (
        selectedShare.gallery.descriptor(mediaId)?.kind === "image"
      ))
      : [];
    return {
      account: this.#account,
      accountDashboardUrl: this.#requestedAccountShareId ? "/account" : undefined,
      accountSignInUrl: this.#accountSignInUrl,
      admission: this.#admission,
      albumDownload: this.#albumDownload?.shareId === this.#catalogue.selectedShareId
        ? this.#albumDownload
        : undefined,
      announcement: this.#announcement,
      contribution: this.#contribution?.shareId === this.#catalogue.selectedShareId
        ? this.#contribution
        : undefined,
      destination: this.#destination,
      mediaSort: this.#mediaSort,
      mediaSortDirection: this.#mediaSortDirection,
      mode: this.#mode,
      openingShell: this.#admission === "connecting"
        && this.#grants.size > 0
        && shares.length === 0,
      photoSelectionActive: selectedShare?.access === "download"
        && this.#photoSelectionActive,
      revokedShareLabel: this.#revokedShareLabel,
      selectedPhotoIds,
      selectedMediaId: this.#selectedMediaId,
      selectedShareId: this.#catalogue.selectedShareId,
      shareMenuOpen: this.#shareMenuOpen,
      shares,
      transferMediaId: this.#activeTransferMediaId,
      transferProgress: this.#transferProgress,
      transferState: this.#transferState,
      videoCapability: this.#videoCapability,
      verificationSubmitting: this.#verificationSubmitting,
    };
  }
}

function sortMediaDescriptorsByCreation(
  media: readonly MediaDescriptor[],
  direction: ViewerUiModel["mediaSortDirection"],
): readonly MediaDescriptor[] {
  return media
    .map((descriptor, index) => ({
      createdAt: descriptor.createdAt ? Date.parse(descriptor.createdAt) : Number.NaN,
      descriptor,
      index,
    }))
    .sort((left, right) => {
      const leftCreatedAt = Number.isFinite(left.createdAt) ? left.createdAt : null;
      const rightCreatedAt = Number.isFinite(right.createdAt) ? right.createdAt : null;
      if (leftCreatedAt === null) {
        return rightCreatedAt === null ? left.index - right.index : 1;
      }
      if (rightCreatedAt === null) return -1;
      const difference = direction === "ascending"
        ? leftCreatedAt - rightCreatedAt
        : rightCreatedAt - leftCreatedAt;
      return difference || left.index - right.index;
    })
    .map(({ descriptor }) => descriptor);
}

function findDialogOpener(opener: DialogOpener | undefined): HTMLElement | undefined {
  if (!opener) return undefined;
  return root!.querySelector<HTMLElement>(
    `[data-action="${opener.action}"][data-media-id="${CSS.escape(opener.mediaId)}"]`,
  ) ?? undefined;
}

function restoreDialogFocus(opener: DialogOpener | undefined): void {
  if (!opener) return;
  requestAnimationFrame(() => findDialogOpener(opener)?.focus());
}

function photoPositionMediaId(event: Event): string | undefined {
  if (!(event instanceof CustomEvent)) return undefined;
  const detail = event.detail;
  return detail
    && typeof detail === "object"
    && "mediaId" in detail
    && typeof detail.mediaId === "string"
    ? detail.mediaId
    : undefined;
}

function photoTileFromEvent(event: Event): HTMLElement | null {
  const target = event.target;
  return target instanceof Element
    ? target.closest<HTMLElement>("[data-action='open-photo']")
    : null;
}

function descriptorMatchesUploaderFilter(
  descriptor: MediaDescriptor,
  filter: string,
): boolean {
  if (filter === "all") return true;
  if (filter === "original") return descriptor.uploadedBy === undefined;
  return descriptor.uploadedBy?.recipientId === filter;
}

function invitationToken(): string | null {
  const match = location.hash.match(/^#([A-Za-z0-9_-]{40,128})$/);
  return match?.[1] ?? null;
}

function accountShareId(): string | null {
  const match = location.pathname.match(
    /^\/shared\/([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})$/,
  );
  return match?.[1] ?? null;
}

function formatDuration(durationMs: number): string {
  const seconds = Math.ceil(durationMs / 1_000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

function formatBytes(byteLength: number): string {
  return `${(byteLength / (1024 * 1024)).toFixed(1)} MB`;
}

function albumDownloadAnnouncement(progress: AlbumDownloadProgress): string {
  if (progress.state === "completed") {
    return `Downloaded ${progress.completed} ${itemLabel(progress.completed)}.`;
  }
  if (progress.state === "cancelled") return "Album download cancelled.";
  return `Downloaded ${progress.completed} of ${progress.total} ${itemLabel(progress.total)}.`;
}

function itemLabel(count: number): string {
  return count === 1 ? "item" : "items";
}

const view = new ViewerApp(root);
const fixture = fixtureFromLocation(window.location);
if (fixture) {
  new FixtureHarness(view, fixture).start();
} else {
  new LiveViewerController(view).start();
}
